import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useGameSettings } from '../contexts/GameSettingsContext'
import { WordLength } from '../types/game'
import { trackModeSelected } from '../services/analyticsManager'
import { trackPageView } from '../utils/analytics'
import { hasModeBeenPlayed, getCompletedGamesDetail } from '../utils/storage'
import { useState, useEffect } from 'react'
import { trackEvent } from '../services/hotjar'
import { isFirstTimePlayer } from '../utils/playerProgression'
import { GAME_MODES } from '../constants/gameConfig'
import { ChallengeCard } from '../components/ChallengeCard'

export const GameModePage = () => {
  const navigate = useNavigate()
  const { updateSettings } = useGameSettings()
  const [playedModes, setPlayedModes] = useState<WordLength[]>([])
  const [isNewPlayer, setIsNewPlayer] = useState<boolean>(false)
  const [completedGames, setCompletedGames] = useState<Record<number, any>>({})

  // Load played modes when component mounts
  useEffect(() => {
    // Check if user is a first-time player
    const firstTimePlayer = isFirstTimePlayer()
    setIsNewPlayer(firstTimePlayer)

    // Get played modes for today
    const modes: WordLength[] = [6, 5, 4, 3].filter((mode) =>
      hasModeBeenPlayed(mode as WordLength)
    ) as WordLength[]
    setPlayedModes(modes)

    // Load completed games details
    const details = getCompletedGamesDetail() || {}
    console.log('--- Completed Games Details Loaded ---', details)
    setCompletedGames(details)

    // Track page view with information about available modes
    try {
      trackPageView({
        page: '/',
        available_modes: [6, 5, 4, 3]
          .filter((mode) => !modes.includes(mode as WordLength))
          .join(','),
        played_modes: modes.join(','),
        is_new_player: firstTimePlayer,
      })

      // Track Hotjar event for mode selection page view
      trackEvent('mode_selection_page_viewed', {
        available_modes: [6, 5, 4, 3]
          .filter((mode) => !modes.includes(mode as WordLength))
          .join(','),
        played_modes: modes.join(','),
        is_new_player: firstTimePlayer,
      })
    } catch (error) {
      console.error('Failed to track mode page view:', error)
    }
  }, [])

  // Check if a mode has been played today
  const isModePlayed = (length: string): boolean => {
    const mode = Number(length) as WordLength
    return playedModes.includes(mode)
  }

  // Handle mode selection with analytics tracking
  const handleModeSelect = (length: string) => {
    const wordLength = Number(length) as WordLength

    // Don't allow selection if mode has been played
    if (isModePlayed(length)) return

    // Track mode selection for analytics
    try {
      trackModeSelected(wordLength)

      // Track Hotjar event for mode selection
      trackEvent('mode_selected', {
        mode: length,
        mode_name:
          GAME_MODES[Number(length) as WordLength]?.label ||
          `${length} Letters`,
      })
    } catch (error) {
      console.error('Failed to track mode selection:', error)
    }

    // Update game settings
    updateSettings({ selectedMode: wordLength })

    // Navigate to the game
    navigate(`/zword/play/${length}`)
  }

  // Animation variants for staggered appearance
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  }

  return (
    <div className="mode-selection">
      <div className="flex justify-center items-center mb-8">
        <h1 className="mode-title">
          Challenge Yourself
        </h1>
      </div>

      {playedModes.length < 3 && (
        <p className="mode-subtitle">
          Select a word length to begin playing
        </p>
      )}

      {isNewPlayer && (
        <div className="mode-subtitle">
          <p className="text-center text-white/90 mb-8 leading-relaxed">
            Choose any challenge you'd like! You can play each challenge once
            daily.
          </p>
        </div>
      )}

      <motion.div
        className="mode-cards"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {Object.entries(GAME_MODES)
          .sort(([lengthA], [lengthB]) => {
            // Sort by difficulty (larger word length first)
            return Number(lengthB) - Number(lengthA)
          })
          .map(([length, mode]) => {
            const played = isModePlayed(length)
            const wordLength = Number(length) as WordLength

            return (
              <motion.div key={length} variants={item}>
                <ChallengeCard
                  wordLength={wordLength}
                  description={mode.description || ''}
                  examples={mode.examples || []}
                  isLocked={false}
                  hasPlayedToday={played}
                  playedGameDetail={completedGames[wordLength]}
                  daysCompleted={0}
                  totalDays={0}
                  onPlay={() => handleModeSelect(length)}
                />
              </motion.div>
            )
          })}
      </motion.div>
    </div>
  )
}
